import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { ExamBar } from './ExamBar';
import { expandedSubQuestions, questionItemCount, questionNumbering, type NumberedQuestion } from './questionNumbering';

interface PaletteSection {
    id: string;
    title: string;
    questions: NumberedQuestion[];
}

export interface PaletteSectionMeta {
    section: PaletteSection;
    /** Global index of this section's first question (offset into the flat list). */
    start: number;
}

export type PaletteVariant = 'rail' | 'sheet';

export interface PaletteDrawerProps {
    sectionMeta: PaletteSectionMeta[];
    answeredCount: number;
    totalQuestions: number;
    currentQuestionIndex: number;
    isAnswered: (questionId: string) => boolean;
    flaggedQuestions: Set<string>;
    /** When false (forced-linear / per-question pacing) only the current tile is reachable. */
    allowFlexibleNav: boolean;
    /** `subQuestionId` is set when a numbered passage sub-question was picked. */
    onGoToQuestion: (index: number, subQuestionId?: string) => void;
    /** Number every passage sub-question instead of one tile per passage. */
    expandPassages?: boolean;
    /** Answered state of a passage sub-question; needed when `expandPassages` is on. */
    isSubAnswered?: (questionId: string, subQuestionId: string) => boolean;
    /** The sub-question last picked in the open passage; defaults to its first. */
    currentSubQuestionId?: string;
    /** `rail`: the desktop side card (6 × 34px tiles, own header); `sheet`: the phone bottom sheet (7 × 38px). */
    variant?: PaletteVariant;
}

interface PaletteTileProps {
    number: number;
    current: boolean;
    /** Another sub-question of the open passage: tinted, not ringed. */
    inOpenPassage?: boolean;
    answered: boolean;
    flagged: boolean;
    disabled: boolean;
    variant: PaletteVariant;
    onClick: () => void;
}

/** Answered = ink, current = white with a 2px ink ring, flagged = amber corner dot. */
export function PaletteTile({ number, current, inOpenPassage = false, answered, flagged, disabled, variant, onClick }: PaletteTileProps) {
    const { t } = useTranslation();
    const label = [`${t('question')} ${number}`, answered ? t('answered') : t('unanswered'), flagged ? t('flagged') : null]
        .filter(Boolean)
        .join(', ');
    return (
        <button
            type="button"
            aria-label={label}
            aria-current={current ? 'true' : undefined}
            disabled={disabled}
            onClick={onClick}
            className={cn(
                'relative inline-flex items-center justify-center rounded-sm font-mono text-[13px] tabular-nums transition-[background-color,box-shadow,color] duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed',
                variant === 'sheet' ? 'h-[38px] w-[38px]' : 'h-[34px] w-[34px]',
                current
                    ? 'bg-card font-bold text-foreground shadow-[inset_0_0_0_2px_hsl(var(--foreground))]'
                    : answered
                      ? 'bg-foreground font-semibold text-background enabled:hover:bg-foreground/85'
                      : inOpenPassage
                        ? 'bg-muted font-medium text-foreground shadow-[inset_0_0_0_1px_hsl(var(--foreground)/0.3)]'
                        : 'bg-card font-medium text-foreground/80 shadow-[inset_0_0_0_1px_hsl(var(--border))] enabled:hover:shadow-[inset_0_0_0_1px_hsl(var(--foreground)/0.3)]',
            )}
        >
            {number}
            {flagged && (
                <span
                    aria-hidden
                    className="absolute -right-[3px] -top-[3px] h-[9px] w-[9px] rounded-full border-2 border-card bg-warning-500"
                />
            )}
        </button>
    );
}

// Legend swatches are mini tiles, so they keep the board's 4px corner.
function PaletteLegend() {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border pt-3.5 text-[12.5px] leading-tight text-foreground/80">
            <span className="flex items-center gap-2">
                <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-[4px] bg-foreground" />
                {t('exam_legend_answered')}
            </span>
            <span className="flex items-center gap-2">
                <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-[4px] border border-foreground/20" />
                {t('unanswered')}
            </span>
            <span className="flex items-center gap-2">
                <span
                    aria-hidden
                    className="h-3.5 w-3.5 shrink-0 rounded-[4px] shadow-[inset_0_0_0_2px_hsl(var(--foreground))]"
                />
                {t('exam_legend_current')}
            </span>
            <span className="flex items-center gap-2">
                <span aria-hidden className="mx-[2.5px] h-[9px] w-[9px] shrink-0 rounded-full bg-warning-500" />
                {t('flagged')}
            </span>
        </div>
    );
}

/** Question palette, shared by the desktop rail and the phone sheet. Sections are always open. All state lives in the parent. */
export function PaletteDrawer({
    sectionMeta,
    answeredCount,
    totalQuestions,
    currentQuestionIndex,
    isAnswered,
    flaggedQuestions,
    allowFlexibleNav,
    onGoToQuestion,
    expandPassages = false,
    isSubAnswered,
    currentSubQuestionId,
    variant = 'rail',
}: PaletteDrawerProps) {
    const { t } = useTranslation();
    const { starts } = questionNumbering(
        sectionMeta.flatMap(({ section }) => section.questions),
        expandPassages,
    );
    const answeredItems = (q: NumberedQuestion) =>
        expandedSubQuestions(q, expandPassages)?.filter((sub) => isSubAnswered?.(q.id, sub.id)).length ??
        (isAnswered(q.id) ? 1 : 0);
    return (
        <div className="flex flex-col">
            {variant === 'rail' && (
                <div className="flex items-center justify-between gap-3 pb-3 leading-tight">
                    <h2 className="text-sm font-semibold">{t('question_navigator')}</h2>
                    <span className="text-[13px] tabular-nums text-muted-foreground">
                        {answeredCount}/{totalQuestions}
                    </span>
                </div>
            )}
            {sectionMeta.map(({ section, start }) => {
                const sectionAnswered = section.questions.reduce((n, q) => n + answeredItems(q), 0);
                const sectionTotal = section.questions.reduce((n, q) => n + questionItemCount(q, expandPassages), 0);
                return (
                    <div key={section.id} className="flex flex-col gap-2.5 border-t border-border py-3.5">
                        <div className="flex items-center justify-between gap-3 text-[13px] leading-tight">
                            <h3 className="truncate font-semibold">{section.title}</h3>
                            <span className="shrink-0 tabular-nums text-muted-foreground">
                                {sectionAnswered}/{sectionTotal}
                            </span>
                        </div>
                        <ExamBar pct={sectionTotal ? (sectionAnswered / sectionTotal) * 100 : 0} className="h-1" />
                        <div
                            className={cn(
                                'grid justify-between',
                                variant === 'sheet'
                                    ? 'grid-cols-[repeat(7,38px)] gap-y-2.5'
                                    : 'grid-cols-[repeat(6,34px)] gap-y-2',
                            )}
                        >
                            {section.questions.map((q, i) => {
                                const globalIdx = start + i;
                                const isCurrent = globalIdx === currentQuestionIndex;
                                const subQuestions = expandedSubQuestions(q, expandPassages);
                                if (subQuestions) {
                                    const focusedId = subQuestions.some((sub) => sub.id === currentSubQuestionId)
                                        ? currentSubQuestionId
                                        : subQuestions[0].id;
                                    return subQuestions.map((sub, j) => (
                                        <PaletteTile
                                            key={sub.id}
                                            number={starts[globalIdx] + j}
                                            current={isCurrent && sub.id === focusedId}
                                            inOpenPassage={isCurrent}
                                            answered={isSubAnswered?.(q.id, sub.id) ?? false}
                                            flagged={flaggedQuestions.has(q.id)}
                                            disabled={!allowFlexibleNav && !isCurrent}
                                            variant={variant}
                                            onClick={() => onGoToQuestion(globalIdx, sub.id)}
                                        />
                                    ));
                                }
                                return (
                                    <PaletteTile
                                        key={q.id}
                                        number={starts[globalIdx]}
                                        current={isCurrent}
                                        answered={isAnswered(q.id)}
                                        flagged={flaggedQuestions.has(q.id)}
                                        disabled={!allowFlexibleNav && !isCurrent}
                                        variant={variant}
                                        onClick={() => onGoToQuestion(globalIdx)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            })}
            <PaletteLegend />
        </div>
    );
}

export default PaletteDrawer;
